import React, { useContext, useEffect, useState } from "react";
import { Actions, ContentContainer, WhiteContainer } from "../../../../../styles/orderform.tsx";
import Summary from "./Summary.tsx";
import VerticalLine from "./VerticalLine.tsx";
import { OrderContext } from "../../OrderContext.tsx";
import Terms from "../Common/Terms.tsx";
import { beginCheckoutEvent } from "../../../../../misc/analytics.ts";
import { getAddress, getVariation } from "../../../../../misc/helpers.ts";

import { styled } from 'styled-components';
import { bp, size } from "../../../../../styles/breakPoints.tsx";
import HorizontalLine from "./HorizontalLine.tsx";
import { MeteringPoint } from "../../types.ts";
import { useTranslation } from "react-i18next";

const SummaryBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  @media ${bp.md} {
    gap: 20px;
    flex-direction: row;
  }

  .green-text {
    font-weight: 700;
    color: var(--Lumme);
  }

  .column {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;

    @media ${bp.md} {
      width: 50%;
    }
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 10px;

  @media ${bp.md} {
    flex-direction: row;
  }

  .point {
    width: 100%;

    @media ${bp.md} {
      width: calc(50% - 27px);
    }

    .green-text {
      font-weight: 700;
      color: var(--Lumme);
    }

    p {
      margin: 6px 0;
    }
  }
`;

const InvoiceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PhaseFiveContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .sub-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
  }
`;


function PhaseFive() {
  const { formValues, products, confirmOrder } = useContext(OrderContext);
  const { t } = useTranslation();

  const [showError, setShowError] = useState(false);
  const [selectedProduct] = useState(getVariation({ products, contractTemplateId: formValues['product'] }));
  const [showButton, setShowButton] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const confirmClick = () => {
    if (!selectedProduct) {
      return
    }
    if (!formValues['accept-terms']) {
      setShowError(true);
      return;
    }
    beginCheckoutEvent(formValues, selectedProduct); // Analytics
    setShowButton(false);
    confirmOrder();
  }

  const locationAddress = getAddress(formValues, 'location');
  const invoicingAddress = formValues['invoice-street'] ? getAddress(formValues, 'invoice') : locationAddress;

  const points = formValues['metering-points'];

  const meteringPointsByRow = (points: MeteringPoint[]) => {
    // first two points belongs to first row
    // the next two points belongs to the second row and so on
    const rows = [];
    for (let i = 0; i < points.length; i += 2) {
      rows.push(points.slice(i, i + 2));
    }
    return rows;
  }

  const pointsByrow = meteringPointsByRow(points);

  return (
    <PhaseFiveContainer>
      <WhiteContainer>
        <Summary></Summary>
      </WhiteContainer>

      <WhiteContainer>
        <SummaryBox>
          <div className="left column">
            <div className="sub-title">{t('phaseFive.customer')}</div>
            <div className="green-text">{formValues['company-name']}</div>
            <div>{t('phaseFive.businessId')} <b>{formValues['organization-no']}</b></div>
            <div><b>{locationAddress}</b></div>
          </div>
          {windowWidth > size.md ? <VerticalLine /> : <HorizontalLine />}
          <div className="right column">
            <div className="sub-title">{t('phaseFive.contactPerson')}</div>
            <div className="green-text">{formValues['contact-firstname']} {formValues['contact-lastname']}</div>
            <div>{t('phaseFive.email')} <b>{formValues['contact-email']}</b></div>
            <div>{t('phaseFive.phone')} <b>{formValues['contact-phone']}</b></div>
          </div>
        </SummaryBox>
      </WhiteContainer>

      <WhiteContainer>
        <div className="sub-title">{t('phaseFive.usagePlaces')}</div>

        {pointsByrow.map((row, index) => {
          const isLastRow = index === pointsByrow.length - 1;
          return (
            <div key={index}>
              <Row>
                {row.map((mp, index) => (
                  <React.Fragment key={index}>
                    <div className="point">
                      <div className="green-text">{mp.address.streetName} {mp.address.houseNumber} {mp.address.houseLetter} {mp.address.residence}, {mp.address.zipCode} {mp.address.cityName}</div>
                      <p>{t('phaseFive.contractStartDate')} <b>{mp.startDate}</b></p>
                    </div>
                    {index == 0 && row.length == 2 && isLastRow == false && (windowWidth > size.md ? <VerticalLine /> : <HorizontalLine />)}
                  </React.Fragment>
                ))}
              </Row>
              {!isLastRow && <HorizontalLine />}
            </div>
          )
        })}

      </WhiteContainer>

      <WhiteContainer>
        <div className="sub-title">{t('phaseFive.invoiceAddress')}</div>
          <InvoiceInfo>
          {formValues['b2b-operator'] && <div>{t('phaseFour.b2bOperatorId')} <b>{formValues['b2b-operator']}</b></div>}
          {formValues['b2b-address'] && <div>{t('phaseFour.einvoiceAddress')} <b>{formValues['b2b-address']}</b></div>}
          {formValues["b2b-email"] && <div>{t('phaseFive.email')} <b>{formValues["b2b-email"]}</b></div>}
          <div><b>{invoicingAddress}</b></div>
          </InvoiceInfo>
      </WhiteContainer>

      <ContentContainer>
        <Terms showError={!formValues['accept-terms'] && showError} />
      </ContentContainer>

      {showButton && <Actions>
        <button
          type="button"
          id="confirm-order"
          className={"primary" + (!formValues['accept-terms'] ? ' disabled' : '')}
          onClick={confirmClick}>
          {t('phaseFour.confirmOrder')}
        </button>
      </Actions>}
    </PhaseFiveContainer>
  );
}

export default PhaseFive;
